/* eslint-disable no-unused-vars */

import React, { useState, useEffect } from 'react';
import propTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchConfiguration } from '../misc/apiRequests';
import Checkmark from '../../assets/images/payments/check-solid.svg';
import Xmark from '../../assets/images/payments/xmark-solid.svg';
import Swal from 'sweetalert2';

const SUBSCRIPTION_DURATIONS = [1, 3, 6, 12];
const DEFAULT_SUBSCRIPTION_MONTHS = 3;

const SubscriptionPlans = ({ user, handleLoader }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [basicMonthlyCost, setBasicMonthlyCost] = useState(0.0);
  const [premiumMonthlyCost, setPremiumMonthlyCost] = useState(0.0);
  const [businessMonthlyCost, setBusinessMonthlyCost] = useState(0.0);
  const [eliteMonthlyCost, setEliteMonthlyCost] = useState(0.0);
  const [maxPropertiesBasic, setMaxPropertiesBasic] = useState(1);
  const [maxPropertiesPremium, setMaxPropertiesPremium] = useState(3);
  const [maxPropertiesBusiness, setMaxPropertiesBusiness] = useState(0);

  // Subscribing needs an account, so send signed out visitors to sign up first.
  const ctaLink = user?.id ? '/account?page=payment' : '/sign_up';

  // A citizen who isn't also a renter has no subscription to buy, and the nav
  // link is hidden for them, so send them home if they reach this URL anyway.
  const citizenOnly = Boolean(user?.roles?.includes('citizen') && !user?.roles?.includes('landlord'));

  useEffect(() => {
    if (citizenOnly) navigate('/');
  }, [citizenOnly, navigate]);

  // api_configs is a public endpoint, so the prices load without a session
  useEffect(() => {
    if (citizenOnly) return;

    handleLoader(true);
    fetchConfiguration()
      .then(response => {
        if (response.success) {
          setMaxPropertiesBasic(response.record.max_properties);
          setMaxPropertiesPremium(response.record.max_properties_premium);
          setMaxPropertiesBusiness(response.record.max_properties_business);
          setBasicMonthlyCost(Number(response.record.basic_subscription_monthly_cost));
          setPremiumMonthlyCost(Number(response.record.premium_subscription_monthly_cost));
          setBusinessMonthlyCost(Number(response.record.business_subscription_monthly_cost));
          setEliteMonthlyCost(Number(response.record.elite_subscription_monthly_cost));
        }
        if (!response.success) {
          Swal.fire({
            position: 'top-end',
            toast: true,
            timerProgressBar: true,
            icon: 'error',
            title: Array.isArray(response.errors) ? response.errors.join("\n") : response.errors,
            showConfirmButton: false,
            timer: 3000
          })
        }
        handleLoader(false);
      });
  }, [handleLoader, citizenOnly]);

  if (citizenOnly) return null;

  const featureLine = (label, included = true) => (
    <li className={`mb-1 ${included ? '' : 'text-red'}`}>
      <img
        className={included ? '' : 'red-filter'}
        src={included ? Checkmark : Xmark}
        style={{width: '14px', marginRight: '6px'}}
        alt={included ? 'checkmark' : 'not included'}
      />
      {label}
    </li>
  );

  // What each prepaid duration works out to, so a visitor can compare
  // without having to start a checkout.
  const durationPricing = (monthlyCost) => (
    <div className='plan-durations mt-2'>
      {SUBSCRIPTION_DURATIONS.map(months => (
        <div
          key={months}
          className={`flex-row justify-flex-between duration-option${months === DEFAULT_SUBSCRIPTION_MONTHS ? ' duration-option-default' : ''}`}
        >
          <span>{months} {months === 1 ? t('month') : t('months')}</span>
          <span>
            <b>${(monthlyCost * months).toFixed(2)}</b>
            {months === DEFAULT_SUBSCRIPTION_MONTHS && <span className='text-grey size-05 ml-1'>{t('most_popular')}</span>}
          </span>
        </div>
      ))}
    </div>
  );

  const planCard = (title, description, monthlyCost, listingLine, featuredIncluded, extraClass = '') => (
    <div className={`subscriber-plan ${extraClass}`}>
      <h3 className='mb-1'>{title}</h3>
      <div className='mb-2'>
        <span>{t('starts_at')}</span>
        <br></br>
        <span><b>${monthlyCost.toFixed(2)}</b> /{t('month')}</span>
      </div>
      <p className='size-05 text-grey mb-2'>{description}</p>
      <ul>
        {featureLine(listingLine)}
        {featureLine(t('tenant_screening'))}
        {featureLine(t('featured_listings'), featuredIncluded)}
      </ul>
      {durationPricing(monthlyCost)}
      <a href={ctaLink} className='plan-cta mt-2'>{t('get_started')}</a>
    </div>
  );

  return (
    <div id='SubscriptionPlansPage'>
      <div className='section-b'>
        <div className='container-md'>
          <div className='messages-container'>
            <h3 className='text-white section-banner section-banner-purple'>{t('subscription_plans')}</h3>
            <p className='text-center mb-2'>{t('plansIntro')}</p>
            <h3 className='mb-2'>{t('choose_plan')}</h3>

            <div className='subscription-plans-grid'>
              {planCard(
                t('basic'), t('basicDesc'), basicMonthlyCost,
                <>{t('allowed_to_list')} <b>{maxPropertiesBasic}</b></>, false
              )}
              {planCard(
                t('premium'), t('premiumDesc'), premiumMonthlyCost,
                <>{t('allowed_to_list')} <b>{maxPropertiesPremium}</b></>, true, 'premium'
              )}
              {planCard(
                t('business'), t('businessDesc'), businessMonthlyCost,
                <>{t('allowed_to_list')} <b>{maxPropertiesBusiness}</b></>, true, 'business'
              )}
              {planCard(
                t('elite'), t('eliteDesc'), eliteMonthlyCost,
                t('allowed_to_list_unlimited'), true, 'business'
              )}
            </div>

            <p className='text-center text-grey mb-3'>{t('plansBillingNote')}</p>

            <div>
              <h3 className='mb-3'>{t('whyChooseTitle')}</h3>
              <p>
                <b>{t('whyChoose1Bold')}</b> – {t('whyChoose1Text')}
              </p>

              <p>
                <b>{t('whyChoose2Bold')}</b> – {t('whyChoose2Text1')}
                <br/>{t('whyChoose2Text2')}
                <br/>
                {t('whyChoose2Text3')}
              </p>

              <p>
                <b>{t('whyChoose3Bold')}</b> – {t('whyChoose3Text')}
              </p>

              <p>
                <b>{t('whyChoose4Bold')}</b> – {t('whyChoose4Text')}
              </p>
            </div>

            <a href={ctaLink} className='plan-cta mt-2 mb-3'>
              {user?.id ? t('plansManageCta') : t('plansSignUpCta')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

SubscriptionPlans.propTypes = {
  user: propTypes.instanceOf(Object),
  handleLoader: propTypes.func.isRequired,
};

export default SubscriptionPlans;
